import React from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconSearch } from '@tabler/icons-react';
import { withImageParams } from 'helpers/imageUrl';

import { useHubSectionTracking } from './useHubSectionTracking';

// Serve the maps through imgdrop without an aspect-crop preset: a 3:2 preset
// would crop the sides off these wide maps, and the banner is wider still, so
// cropping twice left too little of the map visible. Delivering the full wide
// map lets `cover` fill the banner edge-to-edge while keeping the most of it.
const MAP_IMAGE_PARAMS = { service: 'imgdrop' };
export const MAP_DEFAULT_URL = withImageParams(
  'https://images.opendoor.com/source/s3/imgdrop-production/2026-06-1780694796694-04039_map_no_hover.png?service=imgdrop',
  MAP_IMAGE_PARAMS,
);
export const MAP_HOVER_URL = withImageParams(
  'https://images.opendoor.com/source/s3/imgdrop-production/2026-06-1780694796695-23295_map_hover_active.png?service=imgdrop',
  MAP_IMAGE_PARAMS,
);

export interface ExploreNewHomesBannerProps {
  mapDefaultUrl: string;
  /** Cross-faded in on hover when provided; single-map banners omit it. */
  mapHoverUrl?: string;
  analyticsName: string;
  onClick?: () => void;
  /** The pill's call to action. */
  label?: string;
  /** Sizing/radius overrides merged onto the banner card. */
  className?: string;
}

/**
 * Map banner linking to the marketplace: a full-card link with a centered
 * "Explore homes" pill that inverts to brand on hover. Used on the buyer hub
 * dashboard and on the Offer Portal landing.
 */
export const ExploreNewHomesBanner: React.FC<ExploreNewHomesBannerProps> = ({
  mapDefaultUrl,
  mapHoverUrl,
  analyticsName,
  onClick,
  label = 'Explore homes',
  className,
}) => (
  <Col
    href="/homes"
    aria-label={label}
    analyticsName={analyticsName}
    onClick={onClick}
    className={cn(
      'group relative h-42 w-full items-center justify-center overflow-hidden rounded-2xl bg-od-bg-secondary no-underline',
      className,
    )}
  >
    {/* Both maps are rendered (and preloaded) and cross-fade on hover so the
        hover state has no first-paint flash. */}
    <Image
      alt=""
      src={mapDefaultUrl}
      className={cn(
        'absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ease-in-out',
        mapHoverUrl && 'opacity-100 group-hover:opacity-0',
      )}
    />
    {mapHoverUrl ? (
      <Image
        alt=""
        src={mapHoverUrl}
        className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100"
      />
    ) : null}
    <Row className="relative items-center gap-2 rounded-2xl bg-od-bg-primary px-5 py-3 transition-colors duration-200 ease-in-out group-hover:bg-od-bg-accent-brand">
      <IconSearch
        size={20}
        className="text-od-content-primary group-hover:text-od-content-inverse-primary"
      />
      <Text
        as="span"
        className="text-label-md text-od-content-primary group-hover:text-od-content-inverse-primary"
      >
        {label}
      </Text>
    </Row>
  </Col>
);

export const ExploreNewHomesSection: React.FC = () => {
  const trackSectionInteraction = useHubSectionTracking('explore-new-homes');

  return (
    <ExploreNewHomesBanner
      mapDefaultUrl={MAP_DEFAULT_URL}
      mapHoverUrl={MAP_HOVER_URL}
      analyticsName="hub-explore-new-homes"
      onClick={trackSectionInteraction}
    />
  );
};
